import React, {useContext, useEffect, useState} from 'react';
import {useForm} from "react-hook-form";
import {useOktaAuth} from '@okta/okta-react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Typography,
    useToastProvider
} from '@deere/fuel-react';
import {
    CATEGORY,
    CRITERIA,
    FUNCTIONAL_AREAS,
    LIKERT_SCALE_LIST,
    RATING_DESC,
    RESPONSE_TYPE,
    STAKEHOLDERS,
    SUB_CATEGORY, SUB_FUNCTIONAL_AREAS
} from "../shared/Constants";
import WarningIcon from '@material-ui/icons/Warning';
import {useHistory, useParams} from 'react-router-dom';
import {Loading} from "../shared/Loading";
import {ModalDialog} from "../shared/ModalDialog";
import {CustomizedRatings} from "../shared/LikertScale";
import {toCapitalize} from "../shared/utils";
import {DataGrid} from "@mui/x-data-grid";
import {useStyles} from "../shared/styles/ViewCrietria";
import {NotFoundPage} from "../shared/NotFoundPage";
import {useAuthConfig} from "../shared/helper";
import {UserContext} from "./Main";

export const CriteriaView = ({name}) => {
    let authAxios = useAuthConfig();
    const classes = useStyles();
    let history = useHistory();
    let {criteriaId} = useParams();
    const {authState} = useOktaAuth();
    const info = useContext(UserContext);
    const {enqueueToast} = useToastProvider();
    const isCriteriaAdmin = info && info.isCriteriaAdmin;
    const {reset} = useForm();
    const [likertScaleList] = useState(LIKERT_SCALE_LIST);
    const [isLoading, setIsLoading] = useState(true);
    const [isPageError, setIsPageError] = useState(false);
    const [ranges, setRanges] = useState([]);
    const [custLikertRanges, setCustLikertRanges] = useState([]);
    const [maxStars, setMaxStars] = useState(0);

    const initState = {
        responseType: "2",
        status: "Active",
        description: "",
        category: 0,
        subCategory: 0,
        ratings: []
    }
    const [state, setState] = useState(initState);

    const resetState = () => {
        setState(initState);
    }

    const rowsData = ranges.map((item) => {
        return {
            rangeMin: item.rangeMin,
            rangeMax: item.rangeMax,
            valueMin: item.valueMin,
            valueMax: item.valueMax,
            label: item.label,
            id: item.id,
            order: item.order
        };
    });

    const columns = [
        {
            field: 'rangeMin',
            renderHeader: (params) => (
                <Box>
                    <Typography size="lg" variant="label">
                        Points
                    </Typography>
                    <Typography size="md" variant="label" >
                        Range Min
                    </Typography>
                </Box>
            ),
            minWidth: 80,
            maxWidth: 80,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box display="flex" justifyContent="flex-end">
                        <Typography variant="body1" gutterBottom>
                            {`${params.row.rangeMin} %`}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: 'rangeMax',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "28px"}}>
                        Range Max
                    </Typography>
                </Box>
            ),
            minWidth: 80,
            maxWidth: 80,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box display="flex" justifyContent="flex-end">
                        <Typography variant="body1" gutterBottom>
                            {`${params.row.rangeMax} %`}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: '',
            headerName: '',
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            renderCell: () => {
                return (
                    <>
                        <Box style={{
                            borderLeft: "2px solid black",
                            height: "200px",
                            position: "absolute",
                            left: "270px"
                        }}/>
                        <Box style={{borderBottom: "2px solid black", width: "80px"}}/>
                    </>
                )
            }
        },
        {
            field: 'valueMin',
            renderHeader: (params) => (
                <Box>
                    <Typography size="lg" variant="label">
                        Performance
                    </Typography>
                    <Typography size="md" variant="label" >
                        Value Min
                    </Typography>
                </Box>
            ),
            minWidth: 140,
            maxWidth: 140,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" gutterBottom>
                        {params.row.valueMin}
                    </Typography>
                )
            }
        },
        {
            field: 'valueMax',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "28px"}}>
                        Value Max
                    </Typography>
                </Box>
            ),
            minWidth: 150,
            maxWidth: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" gutterBottom>
                        {params.row.valueMax}
                    </Typography>
                )
            }
        },
        {
            field: 'label',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "28px"}}>
                        Label
                    </Typography>
                </Box>
            ),
            minWidth: 250,
            maxWidth: 250,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" gutterBottom>
                        {params.row.label}
                    </Typography>
                )
            }
        }
    ];

    const custLikertRowsData = custLikertRanges.map((item) => {
        return {
            pointsRange: item.pointsRange,
            label: item.label,
            id: item.id,
            order: item.order
        };
    });

    const custLikertColumns = [
        {
            field: 'likertRating',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "28px"}}>
                        Rating
                    </Typography>
                </Box>
            ),
            minWidth: 150,
            maxWidth: 150,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box display="flex" justifyContent="flex-end">
                        <CustomizedRatings selected={params.row.order}
                                           name={`${params.row.id}likertRating`}
                                           readOnly={true}
                                           max={maxStars}
                        />
                    </Box>
                )
            }
        },
        {
            field: 'pointsRange',
            renderHeader: (params) => (
                <Box>
                    <Typography size="lg" variant="label">
                        Points
                    </Typography>
                    <Typography size="md" variant="label" >
                        Range
                    </Typography>
                </Box>
            ),
            minWidth: 120,
            maxWidth: 120,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Box display="flex" justifyContent="flex-end">
                        <Typography variant="body1" gutterBottom>
                            {`${params.row.pointsRange} %`}
                        </Typography>
                    </Box>
                )
            }
        },
        {
            field: 'label',
            renderHeader: (params) => (
                <Box>
                    <Typography size="md" variant="label" style={{marginTop: "28px"}}>
                        Description
                    </Typography>
                </Box>
            ),
            minWidth: 250,
            maxWidth: 250,
            sortable: false,
            renderCell: (params) => {
                return (
                    <Typography variant="body1" gutterBottom>
                        {params.row.label}
                    </Typography>
                )
            }
        }
    ];

    const onUpdate = async () => {
        const data = {status: "ACTIVE"}
        await authAxios.put(`/criteria/update-status/${criteriaId}`, data)
            .then(res => {
                enqueueToast({
                    variant: "success",
                    title: "Criteria reactivated",
                    isCloseable: true
                });
                setTimeout(() => {
                    history.replace(`/criteria-catalog/edit-criteria/${criteriaId}`);
                }, 2000);
            }).catch((error) => {
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: error.response ? error.response.data[error.response.status] : "",
                    isPersistent: true
                });
                console.log(error);
            });
    }

    const onCancel = () => {
        reset();
        history.push("/criteria-catalog/");
    }

    const toCommaValues = arr => {
        return arr.map((item) => {
            return item.description
        }).join(', ');
    }
    const processData = data => {
        setState(s => ({
            ...s,
            responseType: data.responseType,
            status: toCapitalize(data.status),
            criteria: data.criteria,
            description: data.description,
            calculation: data.calculation,
            category: data.category.description,
            subCategory: data.subCategory ? data.subCategory.description : "",
            functionalAreas: toCommaValues(data.functionalAreas),
            subFunctionalAreas: toCommaValues(data.subFunctionalAreas),
            stakeholders: toCommaValues(data.stakeholders),
            ratings: data.ratings
        }));

        if (data.ranges && data.ranges.length > 0) {
            data.ranges.forEach((item) => {
                item.id = item.order;
            });

            setRanges(data.ranges)
        }

        if (data.custLikertRanges && data.custLikertRanges.length > 0) {
            data.custLikertRanges.forEach((item) => {
                item.id = item.order;
            });

            setCustLikertRanges(data.custLikertRanges)
            setMaxStars(data.custLikertRanges.length)
        }
    }

    const getCriteriaDetails = async () => {
        try {
            return await authAxios.get(`/criteria/${criteriaId}`);
        } catch (e) {
            console.error(e.response.status);
            if (e.response.status === 404)
                setIsPageError(true);
            else
                enqueueToast({
                    variant: "error",
                    title: "System Error",
                    message: e.response ? e.response.data[e.response.status] : "",
                    isPersistent: true
                });
        }
    };

    useEffect(() => {
        if (criteriaId) {
            getCriteriaDetails().then(res => {
                if (res) {
                    processData(res.data)
                    setIsLoading(false)
                } else {
                    setIsLoading(false)
                }
            })
        }

        return () => {
            resetState()
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <>
            {!isPageError && (
                <Grid container alignItems="center">
                    <Grid item xs={9}>
                        <Typography variant="h1" component="h1">
                            {name}
                        </Typography>
                    </Grid>
                    <Grid item xs={3}>
                        <Box display="flex" justifyContent="flex-end"
                             alignItems="flex-end">
                            {isCriteriaAdmin && (
                                <ModalDialog
                                    titleText={"Reactivate Criteria"}
                                    saveButtonText={"Reactivate"}
                                    content={"Are you sure you want to reactivate this criteria?"}
                                    onSave={onUpdate}
                                    page={"Criteria"}
                                />
                            )}
                            <Button variant="secondary" size="large" onClick={() => {
                                onCancel()
                            }}>Cancel</Button>
                        </Box>
                    </Grid>
                </Grid>
            )}
            <Grid>
                <Grid item xs={12}>
                    {isLoading && <Loading/>}

                    {!isLoading && (
                        <>
                            {isPageError ? (
                                <NotFoundPage backPage={"criteria-catalog"}/>
                            ) : (
                                <Box m={4}>
                                    <Paper>
                                        <Box p={6}>
                                            <Grid container alignItems="flex-start" spacing={6}>
                                                <Grid item xs={3}>
                                                    <Typography variant="label" gutterBottom>
                                                        {CATEGORY}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {state.category}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="label" gutterBottom>
                                                        {SUB_CATEGORY}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {state.subCategory}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Typography variant="label" gutterBottom>
                                                        {RESPONSE_TYPE}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {state.responseType.responseType}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={3}>
                                                    <Box display="flex" justifyContent="flex-end" alignItems="flex-end">
                                                        {state.status === "Inactive" && (
                                                            <Typography variant="body1" gutterBottom>
                                                                <WarningIcon
                                                                    style={{fill: "#fab64c"}}/>{`${state.status} Criteria`}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="label" gutterBottom>
                                                        {FUNCTIONAL_AREAS}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {state.functionalAreas}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={6}>
                                                    <Typography variant="label" gutterBottom>
                                                        {SUB_FUNCTIONAL_AREAS}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {state.subFunctionalAreas}
                                                    </Typography>
                                                </Grid>
                                                {(state.responseType.id === 1 || state.responseType.id === 2 || state.responseType.id === 4) && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="label" gutterBottom>
                                                            {STAKEHOLDERS}
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {state.stakeholders}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                    <Typography variant="label" gutterBottom>
                                                        {CRITERIA}
                                                    </Typography>
                                                    <Typography variant="body1" gutterBottom>
                                                        {state.criteria}
                                                    </Typography>
                                                </Grid>
                                                {(state.responseType.id === 3 || state.responseType.id === 4) && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="label" gutterBottom>
                                                            Description
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {state.description ? state.description : "N/A"}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {(state.responseType.id === 3 || state.responseType.id === 4) && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="label" gutterBottom>
                                                            Calculation
                                                        </Typography>
                                                        <Typography variant="body1" gutterBottom>
                                                            {state.calculation ? state.calculation : "N/A"}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {state.responseType.id === 3 && (
                                                    <Grid item xs={12}>
                                                        <Box style={{marginLeft: '-1.5em'}}>
                                                            <DataGrid
                                                                className={classes.root}
                                                                style={{backgroundColor: "white"}}
                                                                rows={rowsData}
                                                                columns={columns}
                                                                hideFooter
                                                                checkboxSelection={false}
                                                                disableSelectionOnClick
                                                                autoHeight={true}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                )}
                                                {state.responseType.id === 4 && (
                                                    <Grid item xs={12}>
                                                        <Box style={{marginLeft: '-1.5em'}}>
                                                            <DataGrid
                                                                className={classes.root}
                                                                style={{backgroundColor: "white"}}
                                                                rows={custLikertRowsData}
                                                                columns={custLikertColumns}
                                                                hideFooter
                                                                checkboxSelection={false}
                                                                disableSelectionOnClick
                                                                autoHeight={true}
                                                            />
                                                        </Box>
                                                    </Grid>
                                                )}
                                                {state.responseType.id === 2 && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="label">
                                                            {RATING_DESC}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {state.responseType.id === 2 && likertScaleList.map((currentRating, index) => {
                                                    return (
                                                        <React.Fragment key={index}>
                                                            <Grid item xs={12} md={2}>
                                                                <CustomizedRatings selected={currentRating.selected}
                                                                                   readOnly={true}/>
                                                            </Grid>
                                                            <Grid item xs={12} md={10}>
                                                                {state.ratings[index].description === "" && (
                                                                    <Typography variant="body1" color="tertiary"
                                                                                gutterBottom>
                                                                        {"none"}
                                                                    </Typography>
                                                                )}
                                                                {state.ratings[index].description !== "" && (
                                                                    <Typography variant="body1"
                                                                                gutterBottom>
                                                                        {state.ratings[index].description}
                                                                    </Typography>
                                                                )}
                                                            </Grid>
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </Grid>
                                        </Box>
                                    </Paper>
                                </Box>
                            )}
                        </>
                    )}
                </Grid>
            </Grid>
        </>
    );
}
